// src/components/student/EditStudent.jsx
import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ArrowLeft } from 'lucide-react';
import { GRADES } from '../../utils/constants';
import { styles } from '../../styles/theme';
import { Input, Select, Textarea, Checkbox } from '../shared/FormField';

const EditStudent = ({ student, onBack }) => {

  // Universal date converter - handles ANY format and converts to YYYY-MM-DD for date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    let year, month, day;
    
    // Handle MM-DD-YY format
    if (/^\d{2}-\d{2}-\d{2}$/.test(dateString)) {
      [month, day, year] = dateString.split('-');
      year = `20${year}`;
    }
    // Handle YYYY-MM-DD format (already correct)
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle MM-DD-YYYY format
    else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      [month, day, year] = dateString.split('-');
    }
    // Handle MM/DD/YY format
    else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateString)) {
      [month, day, year] = dateString.split('/');
      year = `20${year}`;
    }
    // Handle MM/DD/YYYY format
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      [month, day, year] = dateString.split('/');
    }
    // Handle M/D/YY or M/D/YYYY formats (no leading zeros)
    else if (dateString.includes('/')) {
      const parts = dateString.split('/');
      month = parts[0].padStart(2, '0');
      day = parts[1].padStart(2, '0');
      year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    }
    // Try parsing as ISO string or general date
    else {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          year = date.getFullYear().toString();
          month = String(date.getMonth() + 1).padStart(2, '0');
          day = String(date.getDate()).padStart(2, '0');
        } else {
          return '';
        }
      } catch (e) {
        console.error('Date parsing error:', e);
        return '';
      }
    }
    
    // Ensure proper formatting
    if (year && month && day) {
      month = month.padStart(2, '0');
      day = day.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return '';
  };

  // Universal date converter - handles ANY format and converts to MM-DD-YY for storage
  const formatDateForStorage = (dateString) => {
    if (!dateString) return '';
    
    let year, month, day;
    
    // Handle YYYY-MM-DD format (from date input)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      [year, month, day] = dateString.split('-');
      year = year.slice(-2);
    }
    // Handle MM-DD-YY format (already correct)
    else if (/^\d{2}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle MM-DD-YYYY format
    else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      [month, day, year] = dateString.split('-');
      year = year.slice(-2);
    }
    // Handle MM/DD/YY format
    else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateString)) {
      [month, day, year] = dateString.split('/');
    }
    // Handle MM/DD/YYYY format
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      [month, day, year] = dateString.split('/');
      year = year.slice(-2);
    }
    // Handle M/D/YY or M/D/YYYY formats (no leading zeros)
    else if (dateString.includes('/')) {
      const parts = dateString.split('/');
      month = parts[0].padStart(2, '0');
      day = parts[1].padStart(2, '0');
      year = parts[2].length === 2 ? parts[2] : parts[2].slice(-2);
    }
    // Try parsing as ISO string or general date
    else {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          year = String(date.getFullYear()).slice(-2);
          month = String(date.getMonth() + 1).padStart(2, '0');
          day = String(date.getDate()).padStart(2, '0');
        } else {
          return '';
        }
      } catch (e) {
        console.error('Date parsing error:', e);
        return '';
      }
    }
    
    // Ensure proper formatting
    if (year && month && day) {
      month = month.padStart(2, '0');
      day = day.padStart(2, '0');
      return `${month}-${day}-${year}`;
    }
    
    return '';
  };
  
  const [formData, setFormData] = useState({
    // Student Info - handle both old and new field names
    studentFirstName: student?.studentFirstName || student?.firstName || '',
    studentLastName: student?.studentLastName || student?.lastName || '',
    grade: student?.grade || '',
    dateOfBirth: formatDateForInput(student?.dateOfBirth) || '',
    gender: student?.gender || '',
    learningDisabilities: student?.learningDisabilities || 'None',
    learningDisabilitiesOther: student?.learningDisabilitiesOther || '',
    medicalConditionsType: student?.medicalConditionsType || 'None',
    medicalConditions: student?.medicalConditions || '',
    foodAllergies: student?.foodAllergies || 'None',
    otherAllergies: student?.otherAllergies || 'None',
    
    // Program Info
    programSelection: student?.programSelection || '',
    programType: student?.programType || '',
    programDaysPerWeek: student?.programDaysPerWeek || '',
    startDate: formatDateForInput(student?.startDate) || '',
    needsPickup: student?.needsPickup || false,
    pickupDays: student?.pickupDays || '',
    campStartDate: formatDateForInput(student?.campStartDate) || '',
    campEndDate: formatDateForInput(student?.campEndDate) || '',
    billingType: student?.billingType || '',
    specialInstructions: student?.specialInstructions || '',
    
    // Parent Info - handle both old and new formats
    parentFirstName: student?.parentFirstName || (student?.parentName ? student.parentName.split(' ')[0] : '') || '',
    parentLastName: student?.parentLastName || (student?.parentName ? student.parentName.split(' ').slice(1).join(' ') : '') || '',
    parentPhone: student?.parentPhone || '',
    parentEmail: student?.parentEmail || '',
    relationshipToStudent: student?.relationshipToStudent || 'Parent',
    homeAddress: student?.homeAddress || '',
    authorizedPickup: student?.authorizedPickup || '',
    
    // School Info
    schoolName: student?.schoolName || '',
    schoolAddress: student?.schoolAddress || '',
    schoolPhone: student?.schoolPhone || '',
    dismissalTimeNormal: student?.dismissalTimeNormal || '15:15',
    dismissalTimeWednesday: student?.dismissalTimeWednesday || '14:15',

    // Agreements - normalize all possible values
    photoRelease: (() => {
      const val = student?.photoRelease;
      if (val === true || val === 'Yes' || val === 'yes' || val === 'true') return 'Yes';
      if (val === false || val === 'No' || val === 'no' || val === 'false') return 'No';
      return '';
    })(),
    photoReleaseParty: student?.photoReleaseParty || false,
    agreementAccepted: (() => {
      const val = student?.agreementAccepted;
      return val === true || val === 'Yes' || val === 'yes' || val === 'true' || val === 'checked';
    })(),
    parentSignature: student?.parentSignature || '',
    termsAgreed: student?.termsAgreed || false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleProgramChange = (value) => {
    let programType = '';
    
    // Map program selection to programType based on actual program IDs
    if (value === 'Full-Day Camp' || value.includes('Camp') || value.includes('Break') || value.includes('Holiday')) {
      programType = 'full-day-camp';
    } else if (value === 'After School Enrichment' || value.includes('Drop-off') || value.includes('Dropoff')) {
      programType = 'after-school-dropoff';
    } else if (value === 'After School Program' || value.includes('Pickup') || value.includes('Pick-up')) {
      programType = 'after-school-pickup';
    } else if (value === 'Saturday Classes' || value.includes('Saturday')) {
      programType = 'saturday-classes';
    } else if (value.includes('Bright Horizons')) {
      programType = 'third-party-billing';
    } else if (value.includes('Birthday')) {
      programType = 'birthday-parties';
    } else {
      programType = 'after-school-dropoff'; // default
    }
    
    setFormData({ 
      ...formData, 
      programSelection: value,
      programType: programType 
    });
  };

  // In EditStudent.jsx - Update the handleSubmit function

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setError('');

  try {
    const studentRef = doc(db, 'students', student.id);
    
    // Prepare data with ALL dates converted to MM-DD-YY format
    const updateData = {
      ...formData,
      dateOfBirth: formatDateForStorage(formData.dateOfBirth),
      startDate: formatDateForStorage(formData.startDate),
      campStartDate: formatDateForStorage(formData.campStartDate),
      campEndDate: formatDateForStorage(formData.campEndDate),
      updatedAt: new Date().toISOString()
    };
    
    // Remove empty date fields
    if (!updateData.dateOfBirth) delete updateData.dateOfBirth;
    if (!updateData.startDate) delete updateData.startDate;
    if (!updateData.campStartDate) delete updateData.campStartDate;
    if (!updateData.campEndDate) delete updateData.campEndDate;
    
    // REGENERATE careDates array if camp dates changed
    if (updateData.campStartDate && updateData.campEndDate) {
      const [startMonth, startDay, startYear] = updateData.campStartDate.split('-');
      const [endMonth, endDay, endYear] = updateData.campEndDate.split('-');
      
      const startDate = new Date(`20${startYear}`, startMonth - 1, startDay, 12, 0, 0);
      const endDate = new Date(`20${endYear}`, endMonth - 1, endDay, 12, 0, 0);
      
      const careDates = [];
      const current = new Date(startDate);
      
      while (current <= endDate) {
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const year = String(current.getFullYear()).slice(-2);
        
        careDates.push({
          date: `${month}-${day}-${year}`,
          startTime: '09:00 AM',
          endTime: '04:00 PM',
          hours: 7
        });
        
        current.setDate(current.getDate() + 1);
      }
      
      updateData.careDates = careDates;
      console.log(`Regenerated ${careDates.length} care dates from ${updateData.campStartDate} to ${updateData.campEndDate}`);
    }
    
    await updateDoc(studentRef, updateData);
    alert('Student information updated successfully!');
    if (onBack) {
      onBack();
    }
  } catch (err) {
    console.error('Error updating student:', err);
    setError('Failed to update student. Please try again.');
    setIsSubmitting(false);
  }
};

  // Check if this is a camp student
  const isCampStudent = formData.programSelection?.includes('Camp') || 
                        formData.campStartDate || 
                        formData.campEndDate;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        padding: '1.5rem 0',
        color: 'white',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              marginBottom: '1rem',
              padding: '0.5rem'
            }}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
            Edit Student Information
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>
            Update student information
          </p>
        </div>
      </div>

      {/* Form */}
      <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}>
        {error && (
          <div style={{
            ...styles.card,
            backgroundColor: '#fee2e2',
            border: '2px solid #ef4444',
            color: '#991b1b',
            marginBottom: '1.5rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Student Information */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              👤 Student Information
            </h2>
            
            <div style={styles.gridTwoCols}>
              <Input
                label="First Name"
                value={formData.studentFirstName}
                onChange={(e) => handleChange('studentFirstName', e.target.value)}
              />
              <Input
                label="Last Name"
                value={formData.studentLastName}
                onChange={(e) => handleChange('studentLastName', e.target.value)}
              />
            </div>

            <div style={styles.gridTwoCols}>
              <Select
                label="Grade"
                value={formData.grade}
                onChange={(e) => handleChange('grade', e.target.value)}
                options={[ ...GRADES]}
              />
              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              />
            </div>

            <Select
              label="Gender"
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              options={['Male', 'Female', 'Other', 'Prefer not to say']}
            />

            <Select
              label="Learning Disabilities"
              value={formData.learningDisabilities}
              onChange={(e) => handleChange('learningDisabilities', e.target.value)}
              options={['None', 'Yes (please specify below)']}
            />

            {formData.learningDisabilities === 'Yes (please specify below)' && (
              <Textarea
                label="Please Specify Learning Disabilities"
                value={formData.learningDisabilitiesOther}
                onChange={(e) => handleChange('learningDisabilitiesOther', e.target.value)}
              />
            )}

            <Select
              label="Medical Conditions / Allergies"
              value={formData.medicalConditionsType}
              onChange={(e) => handleChange('medicalConditionsType', e.target.value)}
              options={['None', 'Yes (please specify below)']}
            />

            {formData.medicalConditionsType === 'Yes (please specify below)' && (
              <Textarea
                label="Please Specify Medical Conditions"
                value={formData.medicalConditions}
                onChange={(e) => handleChange('medicalConditions', e.target.value)}
                placeholder="List any allergies, medications, or medical conditions"
              />
            )}
          </div>

          {/* Program Information */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              📚 Program Information
            </h2>

            <Select
              label="Program Selection"
              value={formData.programSelection}
              onChange={(e) => handleProgramChange(e.target.value)}
              options={[
                'Full-Day Camp',
                'After School Enrichment',
                'After School Program',
                'Saturday Classes',
                'Bright Horizons Registration Form Only',
                'Birthday Parties'
              ]}
            />

            <Select
              label="Days Per Week"
              value={formData.programDaysPerWeek}
              onChange={(e) => handleChange('programDaysPerWeek', e.target.value)}
              options={['5 days', '4 days', '3 days', '2 days']}
            />

            <Input
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
            />

            {/* Show camp dates for ALL camp students */}
            {isCampStudent && (
              <div style={styles.gridTwoCols}>
                <Input
                  label="Camp Start Date"
                  type="date"
                  value={formData.campStartDate}
                  onChange={(e) => handleChange('campStartDate', e.target.value)}
                />
                <Input
                  label="Camp End Date"
                  type="date"
                  value={formData.campEndDate}
                  onChange={(e) => handleChange('campEndDate', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Parent/Guardian Information */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              👨‍👩‍👧 Parent/Guardian Information
            </h2>

            <div style={styles.gridTwoCols}>
              <Input
                label="First Name"
                value={formData.parentFirstName}
                onChange={(e) => handleChange('parentFirstName', e.target.value)}
              />
              <Input
                label="Last Name"
                value={formData.parentLastName}
                onChange={(e) => handleChange('parentLastName', e.target.value)}
              />
            </div>

            <div style={styles.gridTwoCols}>
              <Input
                label="Phone Number"
                type="tel"
                value={formData.parentPhone}
                onChange={(e) => handleChange('parentPhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
              <Input
                label="Email Address"
                type="email"
                value={formData.parentEmail}
                onChange={(e) => handleChange('parentEmail', e.target.value)}
                placeholder="parent@example.com"
              />
            </div>

            <Input
              label="Home Address"
              value={formData.homeAddress}
              onChange={(e) => handleChange('homeAddress', e.target.value)}
              placeholder="Street address, City, State, ZIP"
            />

            <Select
              label="Relationship to Student"
              value={formData.relationshipToStudent}
              onChange={(e) => handleChange('relationshipToStudent', e.target.value)}
              options={['Parent', 'Guardian', 'Grandparent', 'Other']}
            />

            <Input
              label="Authorized Pickup Persons"
              value={formData.authorizedPickup}
              onChange={(e) => handleChange('authorizedPickup', e.target.value)}
              placeholder="Enter names separated by commas (e.g., John Doe, Jane Smith)"
            />
          </div>

          {/* Pickup Service */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              🚗 Pickup Service
            </h2>

            <Checkbox
              label="My child needs pickup service from school"
              checked={formData.needsPickup}
              onChange={(e) => handleChange('needsPickup', e.target.checked)}
            />

            {formData.needsPickup && (
              <>
                <Input
                  label="School Name"
                  value={formData.schoolName}
                  onChange={(e) => handleChange('schoolName', e.target.value)}
                />

                <Input
                  label="School Address"
                  value={formData.schoolAddress}
                  onChange={(e) => handleChange('schoolAddress', e.target.value)}
                  placeholder="Full street address"
                />

                <Input
                  label="School Phone Number"
                  type="tel"
                  value={formData.schoolPhone}
                  onChange={(e) => handleChange('schoolPhone', e.target.value)}
                />

                <Input
                  label="Pickup Days"
                  value={formData.pickupDays}
                  onChange={(e) => handleChange('pickupDays', e.target.value)}
                  placeholder="e.g., Monday, Tuesday, Friday"
                />

                <div style={styles.gridTwoCols}>
                  <Input
                    label="Normal Dismissal Time"
                    type="time"
                    value={formData.dismissalTimeNormal}
                    onChange={(e) => handleChange('dismissalTimeNormal', e.target.value)}
                  />
                  <Input
                    label="Wednesday Dismissal Time"
                    type="time"
                    value={formData.dismissalTimeWednesday}
                    onChange={(e) => handleChange('dismissalTimeWednesday', e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Agreements */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              📋 Agreements & Permissions
            </h2>

            <Checkbox
              checked={formData.photoRelease}
              onChange={(e) => handleChange('photoRelease', e.target.checked)}
            >
              I consent to photographs/videos of my child being used for marketing purposes
            </Checkbox>

            <Checkbox
              checked={formData.agreementAccepted}
              onChange={(e) => handleChange('agreementAccepted', e.target.checked)}
            >
              I agree to the Terms and Conditions and Privacy Policy
            </Checkbox>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                flex: 1,
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: '2px solid #d1d5db',
                background: 'white',
                color: '#374151',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                borderRadius: '0.5rem',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStudent;